import React, { useState, useEffect } from 'react';
import BrandMark from './BrandMark';
import { useNavigate } from 'react-router-dom';
import { getAdmin, logout } from '../auth';
import { profileAPI } from '../api';
import './Profile.css';
import './CeleryTasks.css';

// Профиль администратора: смена пароля + системный Telegram
// (отдельный от пользовательских уведомлений канал для алертов мониторинга).
const Profile = () => {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);

  // Смена пароля
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [pwdMsg, setPwdMsg] = useState(null); // {type: 'ok'|'err', text}
  const [pwdSaving, setPwdSaving] = useState(false);

  // Системный Telegram
  const [tg, setTg] = useState({ has_token: false, bot_token_masked: '', chat_id: '', enabled: false, queue_alert_threshold: 500 });
  const [botToken, setBotToken] = useState('');
  const [chatId, setChatId] = useState('');
  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState(500);
  const [tgMsg, setTgMsg] = useState(null);
  const [tgSaving, setTgSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  // Фича-флаги «Ребалансировка v2»
  const [flags, setFlags] = useState([]);
  const [flagsMsg, setFlagsMsg] = useState(null);
  const [flagBusy, setFlagBusy] = useState(null); // имя переключаемого флага

  useEffect(() => {
    setAdmin(getAdmin());
    profileAPI.getSystemTelegram()
      .then(resp => {
        const d = resp.data;
        setTg(d);
        setChatId(d.chat_id || '');
        setEnabled(!!d.enabled);
        setThreshold(d.queue_alert_threshold || 500);
      })
      .catch(() => setTgMsg({ type: 'err', text: 'Не удалось загрузить настройки Telegram' }));

    profileAPI.getFeatureFlags()
      .then(resp => setFlags(resp.data.flags || []))
      .catch(() => setFlagsMsg({ type: 'err', text: 'Не удалось загрузить фича-флаги' }));
  }, []);

  const toggleFlag = async (name, next) => {
    setFlagsMsg(null);
    setFlagBusy(name);
    // Оптимистично — но при ошибке откатываем: флаг не должен «залипнуть»
    // включённым в интерфейсе, если на сервере он выключен.
    setFlags(prev => prev.map(f => (f.name === name ? { ...f, enabled: next } : f)));
    try {
      const resp = await profileAPI.setFeatureFlag(name, next);
      setFlagsMsg({ type: 'ok', text: resp.data.message + ' — применится на воркерах в течение минуты' });
    } catch (err) {
      setFlags(prev => prev.map(f => (f.name === name ? { ...f, enabled: !next } : f)));
      setFlagsMsg({ type: 'err', text: err.response?.data?.detail || 'Не удалось переключить флаг' });
    } finally {
      setFlagBusy(null);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    setPwdMsg(null);
    if (newPassword.length < 8) {
      setPwdMsg({ type: 'err', text: 'Новый пароль должен быть не короче 8 символов' });
      return;
    }
    if (newPassword !== newPassword2) {
      setPwdMsg({ type: 'err', text: 'Пароли не совпадают' });
      return;
    }
    setPwdSaving(true);
    try {
      await profileAPI.changePassword(currentPassword, newPassword);
      setPwdMsg({ type: 'ok', text: 'Пароль изменён' });
      setCurrentPassword(''); setNewPassword(''); setNewPassword2('');
    } catch (err) {
      setPwdMsg({ type: 'err', text: err.response?.data?.detail || 'Ошибка смены пароля' });
    } finally {
      setPwdSaving(false);
    }
  };

  const saveTelegram = async (e) => {
    e.preventDefault();
    setTgMsg(null);
    if (!chatId.trim()) {
      setTgMsg({ type: 'err', text: 'Укажите chat_id' });
      return;
    }
    if (!tg.has_token && !botToken.trim()) {
      setTgMsg({ type: 'err', text: 'Укажите токен бота' });
      return;
    }
    setTgSaving(true);
    try {
      await profileAPI.saveSystemTelegram({
        bot_token: botToken.trim() || null,
        chat_id: chatId.trim(),
        enabled,
        queue_alert_threshold: Number(threshold) || 500,
      });
      setTgMsg({ type: 'ok', text: 'Настройки сохранены' });
      setBotToken('');
      const resp = await profileAPI.getSystemTelegram();
      setTg(resp.data);
    } catch (err) {
      setTgMsg({ type: 'err', text: err.response?.data?.detail || 'Ошибка сохранения' });
    } finally {
      setTgSaving(false);
    }
  };

  const sendTest = async () => {
    setTgMsg(null);
    setTesting(true);
    try {
      await profileAPI.testSystemTelegram();
      setTgMsg({ type: 'ok', text: 'Тестовое сообщение отправлено — проверьте чат' });
    } catch (err) {
      setTgMsg({ type: 'err', text: err.response?.data?.detail || 'Не удалось отправить' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="profile-page">
      <header className="dashboard-header">
        <div className="header-left">
          <div className="logo">
            <BrandMark size={28} className="logo-icon" />
            <span className="logo-text">Balancify</span>
            <span className="logo-badge">Admin</span>
          </div>
          <span className="ct-page-title">Профиль администратора</span>
        </div>
        <div className="header-right">
          <button onClick={() => navigate('/')} className="btn-logout" title="Вернуться на дашборд">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15 18 9 12 15 6" />
            </svg>
            Дашборд
          </button>
          {admin && (
            <div className="admin-badge">
              <span className="admin-avatar">{admin.username?.charAt(0).toUpperCase()}</span>
              <div className="admin-info">
                <span className="admin-name">{admin.username}</span>
                <span className="admin-role">{admin.role}</span>
              </div>
            </div>
          )}
          <button onClick={logout} className="btn-logout">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
              <polyline points="16 17 21 12 16 7"/>
              <line x1="21" y1="12" x2="9" y2="12"/>
            </svg>
            Выйти
          </button>
        </div>
      </header>

      <main className="profile-content">
        {/* Смена пароля */}
        <section className="profile-card">
          <h2>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Смена пароля
          </h2>
          <form onSubmit={changePassword} className="profile-form">
            <label>
              Текущий пароль
              <input type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} required autoComplete="current-password" />
            </label>
            <label>
              Новый пароль
              <input type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} required autoComplete="new-password" />
            </label>
            <label>
              Новый пароль ещё раз
              <input type="password" value={newPassword2} onChange={e => setNewPassword2(e.target.value)} required autoComplete="new-password" />
            </label>
            {pwdMsg && <div className={`profile-msg ${pwdMsg.type}`}>{pwdMsg.text}</div>}
            <button type="submit" className="btn-primary" disabled={pwdSaving}>
              {pwdSaving ? 'Сохранение...' : 'Изменить пароль'}
            </button>
          </form>
        </section>

        {/* Системный Telegram */}
        <section className="profile-card">
          <h2>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 2L11 13" />
              <path d="M22 2l-7 20-4-9-9-4 20-7z" />
            </svg>
            Системный Telegram
          </h2>
          <p className="profile-hint">
            Отдельный канал для <strong>админских</strong> уведомлений — сюда мониторинг (cron каждые 5 минут)
            шлёт алерты: затор очереди Celery, диск &gt;80%, память &gt;90%, падение сервисов.
            Пользовательские уведомления бота это не затрагивает. Создайте бота через @BotFather,
            добавьте его в свой чат и укажите chat_id.
          </p>
          <form onSubmit={saveTelegram} className="profile-form">
            <label>
              Токен бота
              <input
                type="password"
                value={botToken}
                onChange={e => setBotToken(e.target.value)}
                placeholder={tg.has_token ? `Сохранён: ${tg.bot_token_masked} (пусто = не менять)` : '123456789:AA...'}
                autoComplete="off"
              />
            </label>
            <label>
              Chat ID
              <input type="text" value={chatId} onChange={e => setChatId(e.target.value)} placeholder="-100123456789 или 123456789" />
            </label>
            <label>
              Порог алерта очереди Celery (задач)
              <input type="number" min="50" value={threshold} onChange={e => setThreshold(e.target.value)} />
            </label>
            <label className="profile-check">
              <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)} />
              Уведомления включены
            </label>
            {tgMsg && <div className={`profile-msg ${tgMsg.type}`}>{tgMsg.text}</div>}
            <div className="profile-actions">
              <button type="submit" className="btn-primary" disabled={tgSaving}>
                {tgSaving ? 'Сохранение...' : 'Сохранить'}
              </button>
              <button type="button" className="btn-secondary" onClick={sendTest} disabled={testing || !tg.has_token}>
                {testing ? 'Отправка...' : 'Отправить тестовое сообщение'}
              </button>
            </div>
          </form>
        </section>

        {/* Фича-флаги «Ребалансировка v2» */}
        <section className="profile-card">
          <h2>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
              <line x1="4" y1="22" x2="4" y2="15" />
            </svg>
            Фича-флаги «Ребалансировка v2»
          </h2>
          <p className="profile-hint">
            Поэтапное внедрение. Выключенный флаг = поведение системы такое же, как до внедрения:
            эндпоинт отвечает 404, вкладка не отрисована, фоновые задачи не пишут данные.
            Изменение подхватывается всеми воркерами <strong>в течение минуты</strong> без рестарта.
          </p>
          {flagsMsg && <div className={`profile-msg ${flagsMsg.type}`}>{flagsMsg.text}</div>}
          {flags.length === 0 && !flagsMsg && <div className="profile-hint">Загрузка...</div>}
          <div className="profile-form">
            {flags.map(f => (
              <label key={f.name} className="profile-check" title={f.name}>
                <input
                  type="checkbox"
                  checked={!!f.enabled}
                  disabled={flagBusy === f.name}
                  onChange={e => toggleFlag(f.name, e.target.checked)}
                />
                <span style={{ opacity: 0.6, marginRight: 8 }}>Фаза {f.phase}</span>
                {f.label}
              </label>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};

export default Profile;
